import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { SmartToy, Translate } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

/**
 * Renders a chat message bubble with automatic translation into the viewer's language.
 * Props:
 *   - msg: message object (may contain sourceLanguage + translations map + originalText/content)
 *   - isOwn: boolean, right-aligned when true
 *   - label: label for the other party (e.g. "OSESG" / customer id)
 */
const ChatMessageBubble = ({ msg, isOwn, label }) => {
  const { t, i18n } = useTranslation();
  const viewerLang = i18n.language;
  const srcLang = msg.sourceLanguage || null;
  const translations = msg.translations || {};
  const originalText = msg.originalText || msg.content;

  const preTranslated = srcLang && viewerLang !== srcLang ? translations[viewerLang] : null;
  const [showOriginal, setShowOriginal] = useState(false);
  const [liveTranslated, setLiveTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const needsLive =
      srcLang && viewerLang !== srcLang && !preTranslated && !liveTranslated && !translating && originalText;
    if (needsLive) {
      setTranslating(true);
      api
        .post('/translate', { text: originalText, target: viewerLang, source: srcLang })
        .then((r) => { if (!cancelled) setLiveTranslated(r.data?.data?.text || null); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setTranslating(false); });
    }
    return () => { cancelled = true; };
  }, [srcLang, viewerLang, preTranslated, originalText, liveTranslated, translating]);

  const effective = preTranslated || liveTranslated;
  const display = showOriginal || !effective ? originalText : effective;
  const canToggle = !!effective && srcLang && srcLang !== viewerLang;

  return (
    <Box sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', mb: 1 }}>
      <Box
        sx={{
          maxWidth: '70%', p: 1.5, borderRadius: 2,
          bgcolor: isOwn ? '#1a237e' : msg.isSystemMessage ? '#fff3e0' : '#f5f5f5',
          color: isOwn ? 'white' : 'inherit'
        }}
      >
        {!isOwn && label && (
          <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 0.5 }}>
            {label}
          </Typography>
        )}
        {msg.isSystemMessage && <SmartToy sx={{ fontSize: 16, mr: 0.5 }} />}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {translating && !display ? t('translating') : display}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5, gap: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {new Date(msg.timestamp || msg.createdAt).toLocaleTimeString()}
          </Typography>
          {canToggle && (
            <Button
              size="small"
              startIcon={<Translate sx={{ fontSize: 14 }} />}
              onClick={() => setShowOriginal((v) => !v)}
              sx={{
                py: 0, minHeight: 0, textTransform: 'none', fontSize: 11,
                color: isOwn ? 'rgba(255,255,255,0.85)' : 'primary.main'
              }}
            >
              {showOriginal ? t('show_translation') : t('show_original')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessageBubble;
